import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Download } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { reportsApi } from '@/api/endpoints/reports';
import { revendaApi } from '@/api/endpoints/admin';
import { useFleetStore } from '@/stores/fleet';
import { exportCsv } from './_shared';
import type { ReportSummary } from '@/api/types';

function thisMonthRange(): { from: string; to: string } {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const iso = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return { from: iso(first), to: iso(d) };
}

interface ClienteUso {
  id: number;
  nome: string;
  devices: number;
  ativos: number;
  distKm: number;
  distNm: number;
  horasMotor: number;
  velMax: number;
}

export function UsageReportPage() {
  const init = thisMonthRange();
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [run, setRun] = useState(0);

  const devices = useFleetStore((s) => s.devices);
  const positions = useFleetStore((s) => s.positions);

  const clientesQ = useQuery({ queryKey: ['revenda', 'clientes'], queryFn: () => revendaApi.listClientes() });

  // devices por grupo (groupId) + todos os ids
  const { byGroup, allIds } = useMemo(() => {
    const g = new Map<number, number[]>();
    const ids: number[] = [];
    for (const d of devices.values()) {
      ids.push(d.id);
      const gid = d.groupId ?? 0;
      const arr = g.get(gid) ?? [];
      arr.push(d.id);
      g.set(gid, arr);
    }
    return { byGroup: g, allIds: ids };
  }, [devices]);

  const summaryQ = useQuery({
    queryKey: ['usage-summary', from, to, run],
    enabled: run > 0 && allIds.length > 0,
    queryFn: () =>
      reportsApi.summary({
        deviceId: allIds,
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59`).toISOString(),
      }),
  });

  const rows: ClienteUso[] = useMemo(() => {
    const sumByDevice = new Map<number, ReportSummary>();
    for (const s of summaryQ.data ?? []) sumByDevice.set(s.deviceId, s);
    const out: ClienteUso[] = [];
    for (const c of clientesQ.data ?? []) {
      const gid = c.traccar_group_id ?? -1;
      const ids = byGroup.get(gid) ?? [];
      if (ids.length === 0) continue;
      let distM = 0, horasMs = 0, velMax = 0, ativos = 0;
      for (const id of ids) {
        const s = sumByDevice.get(id);
        if (s) {
          distM += s.distance ?? 0;
          horasMs += s.engineHours ?? 0;
          velMax = Math.max(velMax, s.maxSpeed ?? 0);
        }
        if (positions.get(id)) ativos += 1;
      }
      out.push({
        id: c.id, nome: c.nome, devices: ids.length, ativos,
        distKm: distM / 1000, distNm: distM / 1852,
        horasMotor: horasMs / 3_600_000, velMax,
      });
    }
    return out.sort((a, b) => b.distKm - a.distKm);
  }, [clientesQ.data, byGroup, positions, summaryQ.data]);

  const fmt = (n: number, dec = 1) => n.toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  const totals = useMemo(() => rows.reduce(
    (acc, r) => ({ devices: acc.devices + r.devices, distKm: acc.distKm + r.distKm, horas: acc.horas + r.horasMotor }),
    { devices: 0, distKm: 0, horas: 0 },
  ), [rows]);

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-bold">
          <BarChart3 className="h-5 w-5 text-primary" /> Uso por Cliente
        </h1>
        <p className="text-sm text-muted-foreground">
          Distância, horas de motor e atividade das embarcações de cada cliente no período.
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <label className="space-y-1">
            <span className="block text-xs font-semibold text-muted-foreground">De</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
          </label>
          <label className="space-y-1">
            <span className="block text-xs font-semibold text-muted-foreground">Até</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
          </label>
          <Button onClick={() => setRun((x) => x + 1)} disabled={summaryQ.isFetching}>
            {summaryQ.isFetching ? 'Gerando…' : 'Gerar'}
          </Button>
          {rows.length > 0 && (
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={() => exportCsv(`uso-clientes-${from}_a_${to}.csv`,
                ['cliente', 'embarcacoes', 'ativas', 'dist_km', 'dist_nm', 'horas_motor', 'vel_max_nos'],
                rows.map((r) => [r.nome, r.devices, r.ativos, fmt(r.distKm), fmt(r.distNm), fmt(r.horasMotor), fmt(r.velMax)]))}
            >
              <Download className="h-4 w-4" /> CSV
            </Button>
          )}
        </CardContent>
      </Card>

      {clientesQ.error ? (
        <p className="text-sm text-destructive">Sem permissão (apenas admin/revenda) ou erro ao carregar clientes.</p>
      ) : run === 0 ? (
        <p className="text-sm text-muted-foreground">Escolha o período e clique em <strong>Gerar</strong>.</p>
      ) : summaryQ.isFetching ? (
        <p className="text-sm text-muted-foreground">Calculando uso das embarcações…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum cliente com embarcações no período.</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Cliente</th>
                    <th className="px-3 py-2 text-right">Emb.</th>
                    <th className="px-3 py-2 text-right">Ativas</th>
                    <th className="px-3 py-2 text-right">Distância</th>
                    <th className="px-3 py-2 text-right">Horas motor</th>
                    <th className="px-3 py-2 text-right">Vel. máx</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="px-3 py-2 font-medium">{r.nome}</td>
                      <td className="px-3 py-2 text-right">{r.devices}</td>
                      <td className="px-3 py-2 text-right">{r.ativos}</td>
                      <td className="px-3 py-2 text-right">{fmt(r.distNm)} mn <span className="text-[10px] text-muted-foreground">({fmt(r.distKm)} km)</span></td>
                      <td className="px-3 py-2 text-right">{fmt(r.horasMotor)} h</td>
                      <td className="px-3 py-2 text-right">{fmt(r.velMax)} nós</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-border font-semibold">
                  <tr>
                    <td className="px-3 py-2">Total ({rows.length} clientes)</td>
                    <td className="px-3 py-2 text-right">{totals.devices}</td>
                    <td className="px-3 py-2 text-right">—</td>
                    <td className="px-3 py-2 text-right">{fmt(totals.distKm)} km</td>
                    <td className="px-3 py-2 text-right">{fmt(totals.horas)} h</td>
                    <td className="px-3 py-2 text-right">—</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
